import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useReportActions, useReportsContext, useSaveAs } from "@ryde/redux";
import {
  DATE_TIME_FORMAT_FILE_NAME,
  formatDate,
  isSafari,
  nowTDateISO,
  PLURAL_KEY,
  simpleResultErrorEmptyResponse,
  useTimezone,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { ReportDownloadStyled } from "./reports.styled";

export const ReportDownload: FC = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { reportTemplate } = useReportsContext();
  const { fetchReportCSV } = useReportActions();
  const { saveAs } = useSaveAs();

  const reportTitle = useMemo(() => {
    const dateSuffix = formatDate({
      dateISO: nowTDateISO(timezone),
      dateFormat: DATE_TIME_FORMAT_FILE_NAME,
      timezone,
    });
    return reportTemplate?.translationKey
      ? `${t(reportTemplate?.translationKey)}_${dateSuffix}.csv`
      : `${t(`report.${PLURAL_KEY}`)}_${dateSuffix}.csv`;
  }, [reportTemplate?.translationKey, t, timezone]);

  const handleClick = async (): Promise<void> => {
    const result = await fetchReportCSV({});

    // Backend/transport failure — surface the actual error.
    if (!result.success) {
      warnAndToastSimpleResult(result, t);
      return;
    }

    // Successful response but an empty body — show a distinct "empty response" toast and skip saveAs,
    // which would otherwise write a .csv whose content is the literal "undefined". Very unlikely for a
    // download, but handled separately so a success result never renders a blank/inconsistent error.
    if (!result.data) {
      warnAndToastSimpleResult(simpleResultErrorEmptyResponse(t), t);
      return;
    }

    const type = isSafari() ? "application/csv" : "text/csv";
    saveAs(result.data, reportTitle, type);
  };

  return (
    <ReportDownloadStyled>
      <div className="report_download_text">
        {t("report.DownloadReportDescription")}
      </div>
      <button className="report_download_btn" onClick={handleClick}>
        {t("report.DownloadReport")}
      </button>
    </ReportDownloadStyled>
  );
};
